<template>
  <div id="pageSupport">
    <v-container grid-list-xl fluid>
      <v-layout row wrap>

        <!-- Page header -->
        <v-flex xs12>
          <div class="modern-page-header">
            <div class="modern-page-header__left">
              <v-btn icon flat class="back-btn" @click="$router.go(-1)">
                <v-icon>arrow_back</v-icon>
              </v-btn>
              <div class="modern-page-header__icon-wrap">
                <v-icon class="modern-page-header__icon">headset_mic</v-icon>
              </div>
              <div>
                <h3 class="modern-page-header__title">Support Request</h3>
                <p class="modern-page-header__sub">
                  {{ ticket.company_name || 'Loading…' }}
                  <span v-if="ticket.created_date"> · {{ formatDate(ticket.created_date) }}</span>
                </p>
              </div>
            </div>
            <!-- Priority badge -->
            <div
              v-if="ticket.priority"
              class="priority-badge"
              :class="'priority-badge--' + (ticket.priority || 'low').toLowerCase()"
            >
              <v-icon x-small class="priority-badge__icon">flag</v-icon>
              {{ (ticket.priority || '').toUpperCase() }} PRIORITY
            </div>
          </div>
        </v-flex>

        <v-flex xs12 v-if="!ticket._id">
          <div class="detail-empty">
            <v-icon class="detail-empty__icon">headset_mic</v-icon>
            <p class="detail-empty__text">No support request data available.</p>
            <v-btn depressed color="primary" small @click="$router.go(-1)">Go Back</v-btn>
          </div>
        </v-flex>

        <template v-if="ticket._id">
          <!-- Top row: Subject + Status -->
          <v-flex xs12>
            <v-card class="detail-card">
              <div class="detail-card__header">
                <v-icon class="detail-card__header-icon">chat_bubble_outline</v-icon>
                <span class="detail-card__header-title">Request Details</span>
              </div>
              <v-divider class="detail-divider"></v-divider>
              <div class="detail-card__body">
                <div class="detail-grid">
                  <div class="detail-item">
                    <div class="detail-item__label">Topic</div>
                    <div class="detail-item__value">{{ ticket.topic || '—' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-item__label">Resource</div>
                    <div class="detail-item__value">{{ ticket.resource || '—' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-item__label">Status</div>
                    <div>
                      <div
                        class="status-pill"
                        :class="ticket.status === 'Active' ? 'status-pill--active' : 'status-pill--inactive'"
                      >
                        <span class="status-pill__dot"></span>
                        {{ ticket.status || '—' }}
                      </div>
                    </div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-item__label">Created</div>
                    <div class="detail-item__value">{{ formatDate(ticket.created_date) }}</div>
                  </div>
                </div>

                <div class="detail-item detail-item--full mt-3">
                  <div class="detail-item__label">Subject</div>
                  <div class="detail-item__value detail-item__value--subject">{{ ticket.subject || '—' }}</div>
                </div>

                <div class="detail-item detail-item--full mt-3">
                  <div class="detail-item__label">Message</div>
                  <div class="detail-message-box">
                    {{ ticket.message || '—' }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-flex>

          <!-- School info -->
          <v-flex xs12 md6>
            <v-card class="detail-card">
              <div class="detail-card__header">
                <v-icon class="detail-card__header-icon">business</v-icon>
                <span class="detail-card__header-title">School Information</span>
              </div>
              <v-divider class="detail-divider"></v-divider>
              <div class="detail-card__body">
                <div class="detail-item">
                  <div class="detail-item__label">Company Name</div>
                  <div class="detail-item__value detail-item__value--highlight">{{ ticket.company_name || '—' }}</div>
                </div>
                <div class="detail-item mt-2">
                  <div class="detail-item__label">Company ID</div>
                  <div class="detail-item__value detail-item__value--mono">{{ ticket.company_id || '—' }}</div>
                </div>
              </div>
            </v-card>
          </v-flex>

          <!-- Subscription info -->
          <v-flex xs12 md6>
            <v-card class="detail-card">
              <div class="detail-card__header">
                <v-icon class="detail-card__header-icon">credit_card</v-icon>
                <span class="detail-card__header-title">Subscription</span>
              </div>
              <v-divider class="detail-divider"></v-divider>
              <div class="detail-card__body">
                <div class="detail-item">
                  <div class="detail-item__label">Last Payment</div>
                  <div class="detail-item__value">{{ ticket.last_payment_date || 'N/A' }}</div>
                </div>
                <div class="detail-item mt-2">
                  <div class="detail-item__label">Expiry Date</div>
                  <div class="detail-item__value">{{ ticket.exp_date || 'N/A' }}</div>
                </div>
                <div class="detail-item mt-2">
                  <div class="detail-item__label">Remaining</div>
                  <div>
                    <div
                      class="remaining-badge"
                      :class="getRemainingClass(ticket.remaining)"
                    >{{ ticket.remaining || '0 days left' }}</div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-flex>
        </template>

      </v-layout>
    </v-container>
  </div>
</template>

<script>
import APISupport from "@/mixins/APISupport";
export default {
  mixins: [APISupport],
  data() {
    return {
      ticket: {}
    };
  },
  created() {
    if (this.$route.params.object) {
      this.ticket = this.$route.params.object;
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr || dateStr === 'N/A') return 'N/A';
      try {
        return new Date(dateStr).toLocaleDateString('en-AU', {
          year: 'numeric', month: 'short', day: 'numeric'
        });
      } catch (e) {
        return dateStr;
      }
    },
    getRemainingClass(remaining) {
      const days = parseInt(remaining);
      if (isNaN(days) || days <= 7) return 'remaining-badge--critical';
      if (days <= 30) return 'remaining-badge--warning';
      return 'remaining-badge--ok';
    }
  }
};
</script>

<style scoped lang="css">
/* Page header */
.modern-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.modern-page-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.back-btn {
  color: #64748b !important;
  margin-right: 4px;
}

.modern-page-header__icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0288D1, #01579B);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.modern-page-header__icon {
  color: #ffffff !important;
  font-size: 20px !important;
}

.modern-page-header__title {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.3px;
}

.modern-page-header__sub {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 13px;
  color: #64748b;
  margin: 2px 0 0;
}

/* Priority badge */
.priority-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
}

.priority-badge__icon {
  font-size: 13px !important;
}

.priority-badge--high {
  background: rgba(244, 67, 54, 0.1);
  color: #c62828;
}

.priority-badge--medium {
  background: rgba(251, 140, 0, 0.1);
  color: #e65100;
}

.priority-badge--low {
  background: rgba(67, 160, 71, 0.1);
  color: #2e7d32;
}

/* Detail cards */
.detail-card {
  border-radius: 14px !important;
  overflow: hidden;
  margin-bottom: 4px;
}

.detail-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
}

.detail-card__header-icon {
  color: #64748b !important;
  font-size: 18px !important;
}

.detail-card__header-title {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.detail-divider {
  border-color: #f1f5f9 !important;
}

.detail-card__body {
  padding: 20px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 20px;
}

.detail-item {}

.detail-item--full {
  width: 100%;
}

.detail-item__label {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
}

.detail-item__value {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #334155;
}

.detail-item__value--subject {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.detail-item__value--highlight {
  font-size: 15px;
  font-weight: 700;
  color: #1565C0;
}

.detail-item__value--mono {
  font-family: 'SF Mono', 'Roboto Mono', monospace;
  font-size: 12px;
  color: #475569;
  background: #f1f5f9;
  padding: 3px 8px;
  border-radius: 6px;
  display: inline-block;
}

.detail-message-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 14px;
  color: #334155;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}

.mt-2 { margin-top: 14px; }
.mt-3 { margin-top: 20px; }

/* Status */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 600;
}

.status-pill--active {
  background: rgba(67, 160, 71, 0.1);
  color: #2e7d32;
}

.status-pill--inactive {
  background: rgba(100, 116, 139, 0.1);
  color: #475569;
}

.status-pill__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-pill--active .status-pill__dot { background: #43a047; }
.status-pill--inactive .status-pill__dot { background: #94a3b8; }

/* Remaining badge */
.remaining-badge {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 20px;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 600;
}

.remaining-badge--ok {
  background: rgba(67, 160, 71, 0.1);
  color: #2e7d32;
}

.remaining-badge--warning {
  background: rgba(251, 140, 0, 0.1);
  color: #e65100;
}

.remaining-badge--critical {
  background: rgba(244, 67, 54, 0.1);
  color: #c62828;
}

/* Empty state */
.detail-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  gap: 12px;
  text-align: center;
}

.detail-empty__icon {
  font-size: 56px !important;
  color: #cbd5e1 !important;
}

.detail-empty__text {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 15px;
  color: #94a3b8;
  margin: 0;
}
</style>
